import React, { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, Clock, Play, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatAge = (s) => {
  if (s === null || s === undefined) return '—';
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
};

const Stat = ({ label, value, tone = 'default' }) => {
  const toneClass = {
    default: 'text-gray-800',
    ok: 'text-emerald-700',
    warn: 'text-amber-700',
    bad: 'text-red-700',
  }[tone];
  return (
    <div className="flex flex-col">
      <span className="text-[11px] uppercase tracking-wide text-gray-400">{label}</span>
      <span className={`text-sm font-semibold ${toneClass}`}>{value}</span>
    </div>
  );
};

const SchedulerHealthStrip = ({ onRunNow }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const fetchHealth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/whatsapp/broadcasts/scheduler/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const json = await res.json();
      setData(json);
    } catch (e) {
      console.error('scheduler health fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 15000);
    return () => clearInterval(id);
  }, [fetchHealth]);

  const handleRunNow = async () => {
    try {
      setRunning(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/whatsapp/broadcasts/scheduler/run-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setLastRun({
        ok: res.ok,
        dispatched: json.dispatched,
        errors: json.errors,
        at: new Date().toLocaleTimeString(),
      });
      await fetchHealth();
      onRunNow && onRunNow();
    } catch (e) {
      setLastRun({ ok: false, message: String(e), at: new Date().toLocaleTimeString() });
    } finally {
      setRunning(false);
    }
  };

  if (loading || !data) {
    return (
      <div
        className="bg-white border border-gray-200 rounded-xl p-4 text-sm text-gray-500"
        data-testid="scheduler-health-strip"
      >
        Loading scheduler health…
      </div>
    );
  }

  const healthy = !!data.healthy;
  const overdue = data.counts?.overdueNow ?? 0;
  const scheduled = data.counts?.scheduled ?? 0;
  const sending = data.counts?.sendingNow ?? 0;

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-4"
      data-testid="scheduler-health-strip"
    >
      <div className="flex flex-wrap items-center gap-5">
        <div className="flex items-center gap-2">
          {healthy ? (
            <CheckCircle2 className="text-emerald-600" size={20} data-testid="scheduler-status-ok" />
          ) : (
            <XCircle className="text-red-600" size={20} data-testid="scheduler-status-bad" />
          )}
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-800">
              {healthy ? 'Scheduler healthy' : 'Scheduler degraded'}
            </span>
            <span className="text-[11px] text-gray-500">
              Worker {data.workerAlive ? 'alive' : 'DOWN'} · watchdog{' '}
              {data.watchdogAlive ? 'on' : 'off'}
            </span>
          </div>
        </div>

        <div className="h-8 w-px bg-gray-200" />

        <Stat label="Last tick" value={formatAge(data.tickAgeSeconds)} tone={healthy ? 'ok' : 'warn'} />
        <Stat label="Scheduled" value={scheduled} />
        <Stat label="Sending now" value={sending} />
        <Stat
          label="Overdue"
          value={overdue}
          tone={overdue > 0 ? 'bad' : 'ok'}
        />
        {data.defaultUserTz && (
          <Stat label="Default TZ" value={data.defaultUserTz} />
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={fetchHealth}
            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            title="Refresh health"
            data-testid="scheduler-refresh-btn"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={handleRunNow}
            disabled={running}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-medium"
            data-testid="scheduler-run-now-btn"
          >
            <Play size={14} />
            {running ? 'Running…' : 'Run now'}
          </button>
        </div>
      </div>

      {overdue > 0 && healthy && (
        <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            {overdue} broadcast{overdue === 1 ? '' : 's'} overdue. Next scheduler tick will pick them up within{' '}
            {Math.max(1, Math.round(30 - (data.tickAgeSeconds || 0)))}s — or click <strong>Run now</strong>.
          </span>
        </div>
      )}

      {!healthy && (
        <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            Last tick was {formatAge(data.tickAgeSeconds)} (expected &lt; 60s). The watchdog should respawn the
            worker automatically; if this persists, restart the backend.
          </span>
        </div>
      )}

      {lastRun && (
        <p className="mt-2 text-xs text-gray-500">
          {lastRun.ok ? (
            <>
              <Activity size={12} className="inline mr-1" />
              Last manual run at {lastRun.at}: dispatched {lastRun.dispatched ?? 0}, errors{' '}
              {lastRun.errors ?? 0}.
            </>
          ) : (
            <>
              <Clock size={12} className="inline mr-1" />
              Run at {lastRun.at} failed: {lastRun.message || 'unknown error'}
            </>
          )}
        </p>
      )}
    </div>
  );
};

export default SchedulerHealthStrip;
